import Core from "@/core/Core";
import Utils from "@/core/utils/Utils";
import DateRange from "@/core/types/DateRange";
import DateSimple from "@/core/types/DateSimple";
import Dictionary from "@/core/types/Dictionary";
import ContractsPublicDAO from "@/domains/Contracts/ContractsPublicDAO";
import ContractPublic from "@/domains/Contracts/ContractPublic";
import ContractType from "@/domains/Contracts/ContractType";

/**
 * Сервис для работы с публичными офертами
 */
class ContractsPublicService {
  /** Базовый путь реестра и карточек публичных оферт */
  static baseUrl = '/list/contracts-public';

  /** Ключ localStorage: JSON-массив типов договоров, акцептованных в прошлый раз,
   *  например ["userAgreement", "seller"]. Работать через put/add, не трогать напрямую. */
  private static readonly ACCEPTED_CONTRACTS_KEY = 'acceptedContracts';

  static contractTypeList = [
    {
      id:   'seller',
      name: 'Продавец',
    },
    {
      id:   'buyer',
      name: 'Покупатель',
    },
    {
      id:   'userConfidential',
      name: 'Политика в отношении обработки персональных данных',
    },
    {
      id:   'userAgreement',
      name: 'Пользовательское соглашение',
    },
    {
      id:   'permissionForProcessing',
      name: 'Согласие на обработку ПД',
    },
  ]

  static contractPriorityList = [
    {
      id:   'null',
      name: 'Все',
    },
    {
      id:   true,
      name: 'Только мажорные',
    },
    {
      id:   false,
      name: 'Только минорные',
    },
  ]

  static contractTypeLabelList = {
    seller:                    'Договор продавца',
    sellerQuality:            'Требования к качеству и маркировке',
    sellerShipment:           'Условия доставки',
    buyer:                     'Договор покупателя',
    buyerQuality:             'Договор покупателя',
    userConfidential:         'Политика в отношении обработки персональных данных',
    userAgreement:            'Пользовательское соглашение',
    permissionForProcessing: 'Согласие на обработку ПД',
  }

  /** Справочник актуальных версий публичных договоров по типу.
   * Ленивая загрузка при первом обращении к getActualContract() */
  private static actualContracts = new Dictionary<string, ContractPublic>()
    .useKeyExtractor(contract => contract.type.id);

  /** Читает акцептованные типы из localStorage.
   *  Битая или старая запись → считаем, что акцептов не было */
  static getAcceptedTypes(): string[] {
    let accepted: string[];

    try {
      accepted = JSON.parse(localStorage.getItem(this.ACCEPTED_CONTRACTS_KEY) ?? '[]');
    } catch (error) {
      accepted = [];
    }

    return accepted;
  }

  /** Акцептован ли тип договора */
  static isTypeAccepted(type: string): boolean {
    return this.getAcceptedTypes().includes(type);
  }

  /** Записывает акцепт в localStorage, перезаписывая ключ. Принимает один тип или массив.
   *  Проксируется в LoginForm.saveAgreementFlagsToLocalStorage */
  static putContractIntoLocalStorage(typeOrTypes: string | string[]): void {
    const types = Array.isArray(typeOrTypes) ? typeOrTypes : [typeOrTypes];

    localStorage.setItem(this.ACCEPTED_CONTRACTS_KEY, JSON.stringify(types));
  }

  /** Дописывает акцепт в localStorage со слиянием — существующие не затирает.
   *  Принимает один тип или массив. */
  static addContractIntoLocalStorage(typeOrTypes: string | string[]): void {
    const accepted = this.getAcceptedTypes();
    const types = Array.isArray(typeOrTypes) ? typeOrTypes : [typeOrTypes];

    for (const type of types) {
      if (accepted.includes(type)) {
        // уже акцептован — повторно не пишем
      } else {
        accepted.push(type);
      }
    }

    this.putContractIntoLocalStorage(accepted);
  }

  /**
   * Возвращает актуальную версию публичного договора по типу.
   * При первом обращении загружает справочник с бека, дальше отвечает из кэша.
   * Отсутствие договора — ошибка конфигурации каталога
   */
  static async getActualContract(type: string): Promise<ContractPublic> {
    if (this.actualContracts.size === 0) {
      const contracts = await this.getList(undefined, undefined, undefined, true);

      this.actualContracts.reset(contracts);
    }

    const contract = this.actualContracts.get(type);

    if (contract === undefined) {
      const error = new Error(`Не найдена актуальная версия публичного договора «${type}»`);

      Core.logException(error);

      throw error;
    }

    return contract;
  }

  static async get(type?: ContractType, version?: number, id?: number): Promise<ContractPublic | null> {
    const response = await ContractsPublicDAO.get(type, version, id);

    if (!response.ok) {
      Core.showNotification.error('Ошибка получения публичной оферты');
      return null;
    }

    return Core.utils.hydrate(response.data, ContractPublic.fromServer);
  }

  static async getList(
    params?: Parameters<typeof ContractsPublicDAO.getList>[0]
  ): Promise<ContractPublic[]> {
    const response = await ContractsPublicDAO.getList(params);

    if (!response.ok) {
      Core.showNotification.error('Ошибка получения списка публичных оферт');
      return [];
    }

    return Core.utils.hydrateArray(response.data, ContractPublic.fromServer);
  }

  static async update(contract: ContractPublic): Promise<boolean> {
    const response = await ContractsPublicDAO.update(contract);

    if (response.ok) {
      Core.showNotification.success('Публичная оферта успешно обновлена');
    } else {
      Core.showNotification.error('Ошибка обновления публичной оферты');
    }

    return response.ok;
  }

  static async publish(type: ContractType, publicationDate: DateSimple): Promise<boolean> {
    const response = await ContractsPublicDAO.publish(type, publicationDate);

    if (response.ok) {
      Core.showNotification.success('Публичная оферта успешно опубликована');
    } else {
      Core.showNotification.error('Ошибка публикации публичной оферты');
    }

    return response.ok;
  }

  static async accept(contracts: ContractPublic[]): Promise<boolean> {
    const response = await ContractsPublicDAO.accept(contracts);

    if (response.ok) {
      Core.showNotification.success('Публичные оферты успешно приняты');
    } else {
      Core.showNotification.error('Ошибка принятия публичных оферт');
    }

    return response.ok;
  }

  static async getPdf(type: ContractType, version: number): Promise<void> {
    const response = await ContractsPublicDAO.getPdf(type, version);
    const data = response.data;

    if (response.ok && data && typeof data.data === 'string' && data.data.length > 0) {
      const fileBlob = Utils.file.base64ToBlob(data.data, data.mimeType, data.name);
      Utils.file.downloadBlob(fileBlob);
    } else {
      Core.showNotification.error('Ошибка получения PDF публичной оферты');
    }
  }
}

export default ContractsPublicService;
